import { useState } from "react";
import { Radio, RadioGroup } from "@headlessui/react";
import { Controller, useFormContext } from "react-hook-form";
import clsx from "clsx";
import { useLocation, useNavigate } from "react-router-dom";
import CarrierPackageModal from "../../../../components/modals/CarrierPackageModal";
import { routeEndpoints } from "../../../../routes/routeEndpoints";
import type { ShippingLabelFormValues } from "../../../../types/forms";
import type { ShippingRate } from "../../../../api/shippingService";
import { useUserContext } from "../../../../hooks/useUserContext";
import { useUpdatePreferences } from "../../../../hooks/useUpdatePreferences";
import CarrierFilter from "./carrierFilter";

type PackagingType = ShippingRate["packagingType"];

const packagingTabs: { value: PackagingType | "all"; label: string }[] = [
  { value: "all", label: "All" },
  { value: "own", label: "Your own packaging" },
  { value: "carrier", label: "Carrier-provided" },
];

interface CarrierServiceProps {
  rates: ShippingRate[];
  isLoading: boolean;
  isError: boolean;
}

export default function CarrierService({
  rates,
  isLoading,
  isError,
}: CarrierServiceProps) {
  const { control, setValue, getValues, trigger } =
    useFormContext<ShippingLabelFormValues>();
  const navigate = useNavigate();
  const { search } = useLocation();
  const { user } = useUserContext();
  const { mutate: updatePreferences } = useUpdatePreferences();
  const [selectedCarriers, setSelectedCarriers] = useState<string[]>([]);
  const [isCarrierPackageModalOpen, setIsCarrierPackageModalOpen] =
    useState(false);
  const [packagingFilter, setPackagingFilter] = useState<PackagingType | "all">(
    "all",
  );

  const visibleRates = rates.filter(
    (rate) =>
      (selectedCarriers.length === 0 ||
        selectedCarriers.includes(rate.carrier)) &&
      (packagingFilter === "all" || rate.packagingType === packagingFilter),
  );

  const handleCarriersChange = (carriers: string[]) => {
    setSelectedCarriers(carriers);
    setValue("rateId", "");
  };

  const handlePackagingChange = (packaging: PackagingType | "all") => {
    setPackagingFilter(packaging);
    setValue("rateId", "");
  };

  const goToReview = () =>
    navigate({ pathname: routeEndpoints.reviewPurchase, search });

  const handleReviewClick = async () => {
    const isValid = await trigger([
      "packageDetails",
      "fromAddressId",
      "rateId",
    ]);
    if (!isValid) return;

    const selectedRate = rates.find((rate) => rate.id === getValues("rateId"));
    if (
      selectedRate?.packagingType === "carrier" &&
      !user?.hideCarrierPackageModal
    ) {
      setIsCarrierPackageModalOpen(true);
    } else {
      goToReview();
    }
  };

  const handleCarrierPackageContinue = (doNotShowAgain: boolean) => {
    if (doNotShowAgain) {
      updatePreferences({ hideCarrierPackageModal: true });
    }
    goToReview();
  };

  return (
    <section>
      <h2 className="mb-5 text-lg font-medium text-gray-950">
        Carrier & Service
      </h2>

      <div className="flex flex-col gap-4">
        <CarrierFilter
          selectedCarriers={selectedCarriers}
          onChange={handleCarriersChange}
        />

        <div className="flex flex-wrap gap-2">
          {packagingTabs.map((tab) => (
            <button
              key={tab.value}
              type="button"
              aria-pressed={packagingFilter === tab.value}
              onClick={() => handlePackagingChange(tab.value)}
              className={clsx(
                "h-9 rounded-xl border px-4 text-sm cursor-pointer transition-colors",
                packagingFilter === tab.value
                  ? "border-blue-500 bg-blue-50 text-blue-600"
                  : "border-gray-200 text-gray-950 hover:bg-gray-50",
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <Controller
          name="rateId"
          control={control}
          rules={{ required: "Select a shipping rate" }}
          render={({ field, fieldState }) => (
            <div className="flex flex-col gap-3">
              {isLoading ? (
                <p className="rounded-xl border border-dashed border-gray-200 p-6 text-center text-sm text-gray-500">
                  Loading rates...
                </p>
              ) : isError ? (
                <p className="rounded-xl border border-dashed border-red-200 p-6 text-center text-sm text-red-500">
                  Couldn't load shipping rates. Please try again.
                </p>
              ) : visibleRates.length > 0 ? (
                <RadioGroup
                  value={field.value ?? null}
                  onChange={field.onChange}
                  aria-label="Shipping rate"
                  className="flex max-h-80 flex-col gap-3 overflow-y-auto p-1"
                >
                  {visibleRates.map((rate) => (
                    <Radio
                      key={rate.id}
                      value={rate.id}
                      className={clsx(
                        "group flex flex-col gap-2 rounded-xl border p-4 cursor-pointer transition-colors outline-none md:flex-row md:items-center md:gap-3",
                        "data-checked:border-blue-500 data-checked:bg-blue-50/50 data-focus:ring-2 data-focus:ring-blue-500",
                        fieldState.error ? "border-red-500" : "border-gray-200",
                      )}
                    >
                      <span className="flex items-center justify-between md:contents">
                        <span className="flex size-5 shrink-0 items-center justify-center rounded-full border-2 border-gray-300 group-data-checked:border-blue-600">
                          <span className="size-2.5 rounded-full bg-blue-600 opacity-0 group-data-checked:opacity-100" />
                        </span>
                        <span className="text-sm text-gray-500 group-data-checked:text-blue-600 md:hidden">
                          {rate.deliveryEstimate}
                        </span>
                      </span>
                      <span className="flex flex-1 flex-col gap-1 min-w-0">
                        <span className="text-sm font-medium text-gray-950 group-data-checked:text-blue-600">
                          {rate.service} · {rate.packaging}
                        </span>
                        <span className="hidden text-sm text-gray-500 group-data-checked:text-blue-600 md:block">
                          {rate.deliveryEstimate}
                        </span>
                      </span>
                      <span className="flex shrink-0 items-center gap-2 md:flex-col md:items-end md:gap-1">
                        <span className="text-base font-semibold text-gray-950 group-data-checked:text-blue-600">
                          ${rate.price.toFixed(2)}
                        </span>
                        <span className="text-xs text-green-600">
                          You're saving {rate.savingsPercent}%
                        </span>
                      </span>
                    </Radio>
                  ))}
                </RadioGroup>
              ) : (
                <p className="rounded-xl border border-dashed border-gray-200 p-6 text-center text-sm text-gray-500">
                  No rates match these filters
                </p>
              )}

              {fieldState.error && (
                <p className="text-sm text-red-500">
                  {fieldState.error.message}
                </p>
              )}
            </div>
          )}
        />

        <button
          type="button"
          onClick={handleReviewClick}
          className="mt-2 h-13 w-full rounded-xl text-base font-medium text-white bg-blue-600 hover:bg-blue-700 cursor-pointer transition-colors"
        >
          Review & Purchase
        </button>
      </div>

      <CarrierPackageModal
        isOpen={isCarrierPackageModalOpen}
        onClose={() => setIsCarrierPackageModalOpen(false)}
        onContinue={handleCarrierPackageContinue}
      />
    </section>
  );
}
